import { colors, fonts, radii } from '@steelwake/ui';
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';

import { useSettingsStore } from '../settings/store.js';
import { hudEvents, hudFrame, useHudStore } from './store.js';
import type { HudEvent, HudMech, HudSnapshot, OnlineHud } from './types.js';

const panel: CSSProperties = {
  position: 'absolute',
  background: 'rgba(11, 13, 16, 0.55)',
  border: `1px solid ${colors.border}`,
  borderRadius: radii.md,
  padding: '8px 10px',
  color: colors.text,
  fontSize: 12,
  fontFamily: fonts.ui,
  pointerEvents: 'none',
};

function Bar({
  value,
  max,
  color,
  height = 8,
  testId,
}: {
  value: number;
  max: number;
  color: string;
  height?: number;
  testId?: string;
}) {
  const pct = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
  return (
    <div
      style={{ height, background: 'rgba(255,255,255,0.12)', borderRadius: 2, overflow: 'hidden' }}
    >
      <div
        data-testid={testId}
        data-value={Math.round(value)}
        style={{
          width: `${String(pct)}%`,
          height: '100%',
          background: color,
          transition: 'width 120ms linear',
        }}
      />
    </div>
  );
}

function Labeled({
  label,
  right,
  children,
}: {
  label: string;
  right?: string;
  children: ReactNode;
}) {
  return (
    <div style={{ marginBottom: 6 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          color: colors.textMuted,
          fontSize: 11,
          marginBottom: 2,
        }}
      >
        <span>{label}</span>
        <span>{right}</span>
      </div>
      {children}
    </div>
  );
}

/** Three-zone damage diagram: legs, torso, arms tinted by their accumulated damage. */
function DamageDiagram({ me }: { me: HudMech }) {
  const tint = (f: number) =>
    `rgba(255, ${String(Math.round(200 - f * 170))}, ${String(Math.round(120 - f * 120))}, ${String(0.35 + f * 0.6)})`;
  return (
    <svg
      data-testid="hud-damage-diagram"
      width="44"
      height="64"
      viewBox="0 0 44 64"
      aria-label="damage diagram"
    >
      <rect x="14" y="4" width="16" height="26" rx="3" fill={tint(0)} data-zone="torso" />
      <rect x="3" y="8" width="8" height="20" rx="3" fill={tint(me.armDamage)} data-zone="arms" />
      <rect x="33" y="8" width="8" height="20" rx="3" fill={tint(me.armDamage)} data-zone="arms" />
      <rect x="12" y="33" width="8" height="27" rx="3" fill={tint(me.legDamage)} data-zone="legs" />
      <rect x="24" y="33" width="8" height="27" rx="3" fill={tint(me.legDamage)} data-zone="legs" />
    </svg>
  );
}

function Radar({ snapshot }: { snapshot: HudSnapshot }) {
  const r = 44;
  const range = 60;
  const d = Math.min(1, snapshot.radar.distance / range) * (r - 4);
  const x = r + Math.sin(snapshot.radar.bearing) * d;
  const y = r - Math.cos(snapshot.radar.bearing) * d;
  return (
    <svg
      data-testid="hud-radar"
      width={r * 2}
      height={r * 2}
      viewBox={`0 0 ${String(r * 2)} ${String(r * 2)}`}
      aria-label="radar"
    >
      <circle cx={r} cy={r} r={r - 1} fill="rgba(11,13,16,0.55)" stroke={colors.border} />
      <circle cx={r} cy={r} r={(r - 4) / 2} fill="none" stroke="rgba(255,255,255,0.12)" />
      <line x1={r} y1={r} x2={r} y2={4} stroke="rgba(255,255,255,0.25)" />
      <circle cx={r} cy={r} r={3} fill={snapshot.me.accent} />
      {snapshot.radar.visible ? (
        <circle cx={x} cy={y} r={4} fill={snapshot.enemy.accent} data-testid="hud-radar-enemy" />
      ) : null}
    </svg>
  );
}

function formatTime(seconds: number): string {
  const s = Math.max(0, Math.ceil(seconds));
  return `${String(Math.floor(s / 60))}:${String(s % 60).padStart(2, '0')}`;
}

const FEED_MAX = 4;
/** How long a sound cue's caption stays on screen. */
const CAPTION_MS = 2200;
const FEED_TTL_MS = 6000;
const NUMBER_POOL = 14;

interface FeedEntry {
  id: number;
  text: string;
  until: number;
}

interface BannerState {
  text: string;
  sub?: string;
  until: number;
}

/**
 * Per-frame DOM updates (nameplate, crosshair spread, low-HP vignette, hit marker, damage
 * numbers) and the drained one-shot events (feed, banners). Never sets React state per frame.
 */
function HudLive({ snapshot }: { snapshot: HudSnapshot }) {
  const nameplate = useRef<HTMLDivElement | null>(null);
  const crosshair = useRef<HTMLDivElement | null>(null);
  const vignette = useRef<HTMLDivElement | null>(null);
  const marker = useRef<HTMLDivElement | null>(null);
  const numbers = useRef<(HTMLSpanElement | null)[]>([]);
  const scoreboard = useRef<HTMLDivElement | null>(null);
  const killcam = useRef<HTMLDivElement | null>(null);
  const numberCursor = useRef(0);
  const [feed, setFeed] = useState<FeedEntry[]>([]);
  const [banner, setBanner] = useState<BannerState | null>(null);
  const [caption, setCaption] = useState<{ text: string; until: number } | null>(null);
  const damageNumbers = useSettingsStore((s) => s.settings.damageNumbers);
  const feedId = useRef(0);
  const enemyName = snapshot.enemy.name;
  const myName = snapshot.me.name;

  useEffect(() => {
    let frame = 0;
    let markerUntil = 0;
    const show = (text: string, sub: string | undefined, ms: number) => {
      setBanner({ text, sub, until: performance.now() + ms });
    };
    const pushFeed = (text: string) => {
      const entry = { id: feedId.current++, text, until: performance.now() + FEED_TTL_MS };
      setFeed((f) => [...f.slice(-(FEED_MAX - 1)), entry]);
    };
    const number = (text: string, color: string) => {
      if (!damageNumbers) return;
      const span = numbers.current[numberCursor.current % NUMBER_POOL];
      numberCursor.current++;
      if (!span) return;
      span.textContent = text;
      span.style.color = color;
      span.style.left = `${String(50 + (Math.random() - 0.5) * 12)}%`;
      span.style.top = `${String(44 + (Math.random() - 0.5) * 8)}%`;
      span.classList.remove('sw-float');
      // Restart the CSS animation.
      void span.offsetWidth;
      span.classList.add('sw-float');
    };
    const handle = (e: HudEvent) => {
      switch (e.t) {
        case 'hit':
          markerUntil = performance.now() + 120;
          if (marker.current) marker.current.style.opacity = e.crit ? '1' : '0.75';
          number(
            `${String(Math.round(e.amount))}${e.crit ? '!' : ''}`,
            e.crit ? '#ffd23f' : e.rear ? '#ffb070' : '#ffffff',
          );
          break;
        case 'blocked':
          number('blocked', '#8fb8ff');
          break;
        case 'kill':
          pushFeed(e.killer ? `${e.killer} destroyed ${e.victim}` : `${e.victim} was destroyed`);
          break;
        case 'roundStart':
          show(`Round ${String(e.round)}`, 'Get ready', 2500);
          break;
        case 'live':
          show('Fight', undefined, 1200);
          break;
        case 'roundEnd':
          show(
            e.won ? 'Round won' : 'Round lost',
            e.reason === 'time' ? 'Time' : e.reason === 'suddenDeath' ? 'Sudden death' : undefined,
            3000,
          );
          pushFeed(`Round ${String(e.round)} · ${e.won ? myName : enemyName}`);
          break;
        case 'suddenDeath':
          show('Sudden death', 'Shields down, hull draining', 3000);
          break;
        case 'matchEnd':
          show(e.won ? 'Victory' : 'Defeat', undefined, 4000);
          break;
        case 'overheat':
          show('Overheated', 'Weapons locked', 1500);
          break;
        case 'ultCharged':
          pushFeed('Ultimate ready (E)');
          break;
        case 'abilityUsed':
          pushFeed(`${e.ultimate ? 'Ultimate' : 'Ability'}: ${e.name}`);
          break;
        case 'caption':
          setCaption({ text: e.text, until: performance.now() + CAPTION_MS });
          break;
        default:
          break;
      }
    };
    const tick = () => {
      frame = requestAnimationFrame(tick);
      for (const e of hudEvents.drain()) handle(e);
      const now = performance.now();
      const np = nameplate.current;
      if (np) {
        np.style.opacity = hudFrame.nameplate.visible ? '1' : '0';
        np.style.transform = `translate(${String(Math.round(hudFrame.nameplate.x))}px, ${String(Math.round(hudFrame.nameplate.y))}px) translate(-50%, -100%)`;
        const dist = `${String(Math.round(hudFrame.nameplate.distance))} m`;
        const el = np.querySelector('[data-distance]');
        if (el && el.textContent !== dist) el.textContent = dist;
      }
      const ch = crosshair.current;
      if (ch) {
        const gap = 6 + Math.min(60, hudFrame.crosshairSpread);
        ch.style.setProperty('--gap', `${String(gap)}px`);
        ch.style.borderColor = hudFrame.aimingAtEnemy ? '#ff6a6a' : 'rgba(255,255,255,0.85)';
      }
      if (vignette.current)
        vignette.current.style.opacity = String(Math.min(0.85, hudFrame.lowHp * 1.4));
      if (scoreboard.current) {
        scoreboard.current.style.display = hudFrame.scoreboard ? 'block' : 'none';
      }
      const kc = killcam.current;
      if (kc) {
        kc.style.display = hudFrame.killcam ? 'block' : 'none';
        const name = kc.querySelector('[data-killer]');
        if (name && hudFrame.killcam && name.textContent !== hudFrame.killcam) {
          name.textContent = hudFrame.killcam;
        }
      }
      if (marker.current && now > markerUntil) marker.current.style.opacity = '0';
      setFeed((f) => (f.some((x) => x.until < now) ? f.filter((x) => x.until >= now) : f));
      setBanner((b) => (b && b.until < now ? null : b));
      setCaption((c) => (c && c.until < now ? null : c));
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [damageNumbers, enemyName, myName]);

  // The HUD's keyframes and pseudo-elements live in styles.css: an inline <style> would need
  // the page's CSP nonce (Phase 9).
  return (
    <>
      <div
        ref={vignette}
        data-testid="hud-vignette"
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          opacity: 0,
          background:
            'radial-gradient(ellipse at center, rgba(0,0,0,0) 45%, rgba(180,20,20,0.75) 100%)',
          transition: 'opacity 200ms',
        }}
      />
      <div
        ref={crosshair}
        data-testid="hud-crosshair"
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          width: 0,
          height: 0,
          pointerEvents: 'none',
          color: 'rgba(255,255,255,0.85)',
        }}
      >
        {[0, 90, 180, 270].map((deg) => (
          <span
            key={deg}
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: 2,
              height: 10,
              marginLeft: -1,
              background: 'currentColor',
              transformOrigin: '50% 0',
              transform: `rotate(${String(deg)}deg) translateY(var(--gap, 8px))`,
            }}
          />
        ))}
        <span
          style={{
            position: 'absolute',
            left: -2,
            top: -2,
            width: 4,
            height: 4,
            borderRadius: 2,
            background: 'currentColor',
          }}
        />
      </div>
      <div
        ref={marker}
        data-testid="hud-hitmarker"
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          width: 26,
          height: 26,
          marginLeft: -13,
          marginTop: -13,
          pointerEvents: 'none',
          opacity: 0,
          transition: 'opacity 80ms',
          backgroundImage:
            'linear-gradient(45deg, transparent 46%, #fff 46%, #fff 54%, transparent 54%), linear-gradient(-45deg, transparent 46%, #fff 46%, #fff 54%, transparent 54%)',
        }}
      />
      <div
        data-testid="hud-damage-numbers"
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
      >
        {Array.from({ length: NUMBER_POOL }, (_, i) => (
          <span
            key={i}
            ref={(el) => {
              numbers.current[i] = el;
            }}
            style={{
              position: 'absolute',
              opacity: 0,
              fontFamily: fonts.ui,
              fontWeight: 700,
              fontSize: 18,
              textShadow: '0 0 4px #000',
            }}
          />
        ))}
      </div>
      <div
        ref={nameplate}
        data-testid="hud-nameplate"
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          opacity: 0,
          padding: '2px 8px',
          background: 'rgba(11,13,16,0.6)',
          borderRadius: radii.sm,
          color: snapshot.enemy.accent,
          fontSize: 12,
          fontFamily: fonts.ui,
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
        }}
      >
        <span>{snapshot.enemy.name}</span>{' '}
        <span data-distance style={{ color: colors.textMuted }} />
      </div>
      <div
        ref={killcam}
        data-testid="hud-killcam"
        style={{
          display: 'none',
          position: 'absolute',
          left: '50%',
          top: 72,
          transform: 'translateX(-50%)',
          padding: '6px 14px',
          background: 'rgba(11, 13, 16, 0.75)',
          border: `1px solid ${colors.border}`,
          borderRadius: 4,
          fontSize: 13,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: colors.text,
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
        }}
      >
        Kill cam · <span data-killer />{' '}
        <span style={{ color: colors.textMuted, textTransform: 'none', letterSpacing: 0 }}>
          · any key to skip
        </span>
      </div>
      <div
        ref={scoreboard}
        data-testid="hud-scoreboard"
        style={{
          ...panel,
          display: 'none',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          minWidth: 460,
          fontSize: 13,
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ color: colors.textMuted, textAlign: 'right' }}>
              <th style={{ textAlign: 'left', padding: '4px 0' }}>Pilot</th>
              <th>Mech</th>
              <th>Damage</th>
              <th>Kills</th>
              <th>Accuracy</th>
              <th>Rounds</th>
            </tr>
          </thead>
          <tbody>
            {([snapshot.mySlot, snapshot.mySlot === 0 ? 1 : 0] as const).map((slot) => {
              const mine = slot === snapshot.mySlot;
              const who = mine ? snapshot.me : snapshot.enemy;
              const s = snapshot.stats[slot];
              const accuracy = s.shotsFired > 0 ? Math.round((100 * s.shotsHit) / s.shotsFired) : 0;
              return (
                <tr
                  key={slot}
                  data-testid={`scoreboard-${mine ? 'me' : 'enemy'}`}
                  style={{ borderTop: `1px solid ${colors.border}`, textAlign: 'right' }}
                >
                  <td style={{ textAlign: 'left', padding: '4px 0', color: who.accent }}>
                    {who.name}
                  </td>
                  <td>{who.mechId}</td>
                  <td>{String(Math.round(s.damageDealt))}</td>
                  <td>{String(s.kills)}</td>
                  <td>{String(accuracy)}%</td>
                  <td>{String(snapshot.wins[slot])}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div
        data-testid="hud-captions-live"
        role="status"
        aria-live="polite"
        style={{
          position: 'absolute',
          left: '50%',
          top: '60%',
          transform: 'translate(-50%, 0)',
          padding: '4px 12px',
          background: 'rgba(11, 13, 16, 0.75)',
          borderRadius: 4,
          fontSize: 15,
          letterSpacing: '0.04em',
          color: colors.text,
          pointerEvents: 'none',
          opacity: caption ? 1 : 0,
          transition: 'opacity 150ms',
        }}
      >
        <span data-testid="hud-captions">{caption?.text ?? ''}</span>
      </div>
      <div
        data-testid="hud-feed"
        style={{
          position: 'absolute',
          right: 12,
          top: 120,
          display: 'grid',
          gap: 4,
          pointerEvents: 'none',
        }}
      >
        {feed.map((f) => (
          <div key={f.id} style={{ ...panel, position: 'static', fontSize: 12 }}>
            {f.text}
          </div>
        ))}
      </div>
      {banner ? (
        <div
          data-testid="hud-banner"
          style={{
            position: 'absolute',
            left: '50%',
            top: '28%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            pointerEvents: 'none',
            fontFamily: fonts.ui,
          }}
        >
          <div
            style={{
              fontSize: 42,
              fontWeight: 700,
              letterSpacing: '0.2em',
              color: colors.text,
              textShadow: '0 0 12px #000',
            }}
          >
            {banner.text}
          </div>
          {banner.sub ? (
            <div style={{ fontSize: 14, color: colors.textMuted }}>{banner.sub}</div>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

/** Text for the room's phase when the fight is not simply running. */
function onlineNotice(
  online: OnlineHud,
  mySlot: number,
  hasResult: boolean,
): { text: string; sub: string } | null {
  if (online.connectionLost) {
    return { text: 'CONNECTION LOST', sub: 'Reconnecting to the match server…' };
  }
  switch (online.phase) {
    case 'ended':
      // A result shows the results overlay instead; without one the socket closed on us.
      return hasResult
        ? null
        : { text: 'DISCONNECTED', sub: online.reason ?? 'The connection to the match closed' };
    case 'waiting':
      return { text: 'WAITING FOR OPPONENT', sub: 'The match starts when both pilots are ready' };
    case 'paused': {
      const seconds =
        online.secondsLeft === null ? '' : ` · ${String(Math.ceil(online.secondsLeft))} s`;
      return online.slot === mySlot
        ? { text: 'RECONNECTING', sub: `Your seat is held${seconds}` }
        : { text: 'OPPONENT RECONNECTING', sub: `The fight is paused${seconds}` };
    }
    case 'aborted':
      return { text: 'MATCH ABORTED', sub: online.reason ?? 'The match could not start' };
    default:
      return null;
  }
}

/** Persistent banner for pauses and aborts, plus the optional connection readout. */
function OnlineOverlay({ online }: { online: OnlineHud }) {
  const showNetStats = useSettingsStore((s) => s.settings.showNetStats);
  const mySlot = useHudStore((s) => s.snapshot?.mySlot ?? 0);
  const hasResult = useHudStore((s) => (s.snapshot?.result ?? null) !== null);
  const notice = onlineNotice(online, mySlot, hasResult);
  return (
    <>
      {notice ? (
        <div
          data-testid="hud-online-notice"
          data-online-phase={online.connectionLost ? 'lost' : online.phase}
          style={{
            position: 'absolute',
            left: '50%',
            top: '40%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            pointerEvents: 'none',
            fontFamily: fonts.ui,
          }}
        >
          <div
            style={{
              fontSize: 34,
              fontWeight: 700,
              letterSpacing: '0.2em',
              color: colors.text,
              textShadow: '0 0 12px #000',
            }}
          >
            {notice.text}
          </div>
          <div style={{ fontSize: 14, color: colors.textMuted }}>{notice.sub}</div>
        </div>
      ) : null}
      {showNetStats ? (
        <div
          data-testid="hud-net"
          style={{
            ...panel,
            right: 12,
            top: 12,
            fontSize: 11,
            color: colors.textMuted,
            minWidth: 150,
            whiteSpace: 'nowrap',
          }}
        >
          <div>
            ping {String(Math.round(online.rttMs))} ms · lead {String(online.lead)}
          </div>
          <div>
            late {String(online.late)} · corr {String(online.corrections)} (max{' '}
            {online.maxCorrectionM.toFixed(2)} m) · desync {String(online.desyncs)}
          </div>
          {online.conditions ? (
            <div style={{ color: colors.warning }}>
              sim lag {String(online.conditions.lagMs)} ms · jitter{' '}
              {String(online.conditions.jitterMs)} ms · loss{' '}
              {String(Math.round(online.conditions.loss * 100))} %
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

/** The in-match HUD (BUILD_PLAN section 7): bars, diagram, weapons, abilities, radar, timer, score. */
export function Hud({ showHint }: { showHint: boolean }) {
  const snapshot = useHudStore((s) => s.snapshot);
  if (!snapshot) return null;
  const { me, enemy } = snapshot;
  const countdown = snapshot.phase === 'countdown';
  return (
    <div
      data-testid="hud"
      data-phase={snapshot.phase}
      style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
    >
      <HudLive snapshot={snapshot} />
      {snapshot.online ? <OnlineOverlay online={snapshot.online} /> : null}

      <div
        data-testid="hud-timer"
        style={{
          ...panel,
          left: '50%',
          top: 12,
          transform: 'translateX(-50%)',
          textAlign: 'center',
          minWidth: 160,
        }}
      >
        <div
          style={{
            fontSize: 22,
            fontWeight: 700,
            letterSpacing: '0.1em',
            color: snapshot.suddenDeath ? colors.danger : colors.text,
          }}
        >
          {countdown
            ? `Round ${String(snapshot.round)} in ${String(Math.ceil(snapshot.timeLeft))}`
            : formatTime(snapshot.timeLeft)}
        </div>
        <div data-testid="hud-score" style={{ color: colors.textMuted }}>
          {me.name} <b style={{ color: me.accent }}>{snapshot.wins[snapshot.mySlot]}</b> ·{' '}
          <b style={{ color: enemy.accent }}>{snapshot.wins[snapshot.mySlot === 0 ? 1 : 0]}</b>{' '}
          {enemy.name}
          {snapshot.suddenDeath ? ' · sudden death' : ''}
        </div>
      </div>

      <div data-testid="hud-enemy" style={{ ...panel, left: 12, top: 12, width: 200 }}>
        <div
          style={{ color: enemy.accent, fontWeight: 700, letterSpacing: '0.06em', marginBottom: 4 }}
        >
          {enemy.name} {enemy.cloaked ? '(cloaked)' : ''}
        </div>
        <Labeled label="Hull" right={`${String(Math.round(enemy.hp))} / ${String(enemy.maxHp)}`}>
          <Bar
            value={enemy.hp}
            max={enemy.maxHp}
            color={snapshot.colours.enemyHull}
            testId="hud-enemy-hp"
          />
        </Labeled>
        <Bar
          value={enemy.shield}
          max={enemy.maxShield}
          color={snapshot.colours.shield}
          height={4}
          testId="hud-enemy-shield"
        />
      </div>

      <div style={{ position: 'absolute', right: 12, top: 12, marginRight: 96 }}>
        <Radar snapshot={snapshot} />
      </div>

      <div
        data-testid="hud-me"
        style={{
          ...panel,
          left: 12,
          bottom: 12,
          width: 260,
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          gap: 10,
        }}
      >
        <div>
          <Labeled label="Hull" right={`${String(Math.round(me.hp))} / ${String(me.maxHp)}`}>
            <Bar
              value={me.hp}
              max={me.maxHp}
              color={me.hp / me.maxHp < 0.35 ? snapshot.colours.hullLow : snapshot.colours.hull}
              height={10}
              testId="hud-hp"
            />
          </Labeled>
          <Labeled label="Shield" right={`${String(Math.round(me.shield))}`}>
            <Bar
              value={me.shield}
              max={me.maxShield}
              color={snapshot.colours.shield}
              height={6}
              testId="hud-shield"
            />
          </Labeled>
          <Labeled label="Energy" right={`${String(Math.round(me.energy))}`}>
            <Bar
              value={me.energy}
              max={me.maxEnergy}
              color={snapshot.colours.energy}
              height={5}
              testId="hud-energy"
            />
          </Labeled>
          <Labeled
            label={me.overheated ? 'Heat · OVERHEATED' : me.venting ? 'Heat · venting' : 'Heat'}
            right={`${String(Math.round(me.heat))}`}
          >
            <Bar
              value={me.heat}
              max={me.heatCap}
              color={me.overheated ? snapshot.colours.hullLow : snapshot.colours.heat}
              height={6}
              testId="hud-heat"
            />
          </Labeled>
          {me.effects.length > 0 ? (
            <div data-testid="hud-effects" style={{ color: colors.textMuted, fontSize: 11 }}>
              {me.effects.join(' · ')}
            </div>
          ) : null}
        </div>
        <DamageDiagram me={me} />
      </div>

      <div data-testid="hud-weapons" style={{ ...panel, right: 12, bottom: 12, width: 240 }}>
        {snapshot.weapons.map((w) => (
          <div
            key={w.slot}
            data-testid={`hud-weapon-${w.slot}`}
            style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}
          >
            <span>
              {w.name}{' '}
              <span style={{ color: colors.textMuted }}>
                ({w.slot === 'primary' ? 'LMB' : 'RMB'})
              </span>
            </span>
            <span data-testid={`hud-ammo-${w.slot}`}>
              {w.reloading
                ? 'reloading…'
                : w.magazine !== null
                  ? `${String(w.ammo ?? 0)} / ${String(w.magazine)}`
                  : w.cooling > 0
                    ? 'cooling'
                    : w.kind}
            </span>
          </div>
        ))}
        <div
          data-testid="hud-ability"
          data-ready={snapshot.ability.cooldown <= 0}
          style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}
        >
          <span>
            Q · {snapshot.ability.name}
            {snapshot.ability.toggled ? ' (on)' : ''}
          </span>
          <span
            style={{
              color: snapshot.ability.cooldown > 0 ? colors.textMuted : snapshot.colours.ready,
            }}
          >
            {snapshot.ability.cooldown > 0 ? `${snapshot.ability.cooldown.toFixed(1)} s` : 'ready'}
          </span>
        </div>
        <Labeled
          label={`E · ${snapshot.ultimate.name}`}
          right={
            snapshot.ultimate.charge >= 100
              ? 'READY'
              : `${String(Math.round(snapshot.ultimate.charge))} %`
          }
        >
          <Bar
            value={snapshot.ultimate.charge}
            max={100}
            color={snapshot.ultimate.charge >= 100 ? '#ffd23f' : '#b892ff'}
            height={6}
            testId="hud-ult"
          />
        </Labeled>
        <div style={{ color: colors.textMuted, fontSize: 11 }}>R vent · F melee · C shoulder</div>
      </div>

      {showHint ? (
        <div
          data-testid="play-hint"
          style={{
            position: 'absolute',
            left: '50%',
            bottom: 12,
            transform: 'translateX(-50%)',
            padding: '4px 10px',
            background: 'rgba(11, 13, 16, 0.5)',
            borderRadius: radii.sm,
            color: colors.textMuted,
            fontSize: 11,
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
          }}
        >
          Click to aim · LMB fire · Q ability · E ultimate · R vent · F melee · Esc settings · F3
          stats
        </div>
      ) : null}
    </div>
  );
}
